import type { Dayjs } from 'dayjs'

// types
import type { CalendarEvent, CalendarViewType, CalendarWeekStart } from '@/types/components/calendar'

// utilities
import { getEventEnd, getEventStart, startOfWeek } from '@/utilities/calendar/datetime'

/**
 * Formats a single time of day.
 * @param date - Moment to format
 * @param hour12 - Use 12-hour clock with am/pm
 */
export function formatTime(date: Dayjs, hour12 = false): string {
  return date.format(hour12 ? 'h:mma' : 'HH:mm')
}

/**
 * Formats an hour label for the time-grid axis.
 * @param minute - Minutes from midnight
 * @param hour12 - Use 12-hour clock with am/pm
 */
export function formatSlotLabel(minute: number, hour12 = false): string {
  const hour = Math.floor(minute / 60) % 24
  const minuteOfHour = minute % 60
  const padded = `${String(hour).padStart(2, '0')}:${String(minuteOfHour).padStart(2, '0')}`

  // 24-hour clock needs no further work
  if (!hour12) {
    return padded
  }

  const suffix = hour < 12 ? 'am' : 'pm'
  const hour12Value = hour % 12 === 0 ? 12 : hour % 12

  // Drop ':00' on whole hours to keep the axis narrow
  return minuteOfHour === 0 ? `${hour12Value}${suffix}` : `${hour12Value}:${String(minuteOfHour).padStart(2, '0')}${suffix}`
}

/**
 * Formats the time shown on an event chip.
 * All-day events return an empty string so the chip renders title-only.
 * @param event - Event to describe
 * @param options - Display options
 * @param options.hour12 - Use 12-hour clock
 * @param options.showEnd - Include the end time
 * @param options.timezone - Calendar-level fallback timezone
 */
export function formatEventTime(
  event: CalendarEvent,
  options: { hour12?: boolean; showEnd?: boolean; timezone?: string } = {}
): string {
  const { hour12 = false, showEnd = false, timezone } = options

  // All-day events have no meaningful clock time
  if (event.allDay) {
    return ''
  }

  const start = getEventStart(event, timezone)

  // Unparseable events fall back to no label rather than 'Invalid Date'
  if (!start) {
    return ''
  }

  // Start-only is the compact form used on chips
  if (!showEnd) {
    return formatTime(start, hour12)
  }

  const end = getEventEnd(event, timezone)

  return end ? `${formatTime(start, hour12)} - ${formatTime(end, hour12)}` : formatTime(start, hour12)
}

/**
 * Formats the full date/time line shown in the event popover.
 * @param event - Event to describe
 * @param options - Display options
 * @param options.hour12 - Use 12-hour clock
 * @param options.timezone - Calendar-level fallback timezone
 */
export function formatEventDateTime(
  event: CalendarEvent,
  options: { hour12?: boolean; timezone?: string } = {}
): string {
  const { hour12 = false, timezone } = options
  const start = getEventStart(event, timezone)

  // Nothing sensible to show without a start
  if (!start) {
    return ''
  }

  const datePart = start.format('dddd, D MMMM YYYY')

  // All-day events show the date alone
  if (event.allDay) {
    return datePart
  }

  const end = getEventEnd(event, timezone)

  // Events ending on another day need the full end date, not just its time
  if (end && !end.isSame(start, 'day')) {
    return `${datePart} ${formatTime(start, hour12)} - ${end.format('D MMM YYYY')} ${formatTime(end, hour12)}`
  }

  return end
    ? `${datePart} - ${formatTime(start, hour12)} to ${formatTime(end, hour12)}`
    : `${datePart} - ${formatTime(start, hour12)}`
}

/**
 * Builds the toolbar's period title for the active view.
 * Week titles collapse shared month and year segments, e.g. '3 - 9 Nov 2025'.
 * @param cursor - Date anchoring the visible period
 * @param view - Active view
 * @param weekStart - 0 = Sunday through 6 = Saturday
 */
export function formatPeriodTitle(cursor: Dayjs, view: CalendarViewType, weekStart: CalendarWeekStart = 1): string {
  // Month view names the month only
  if (view === 'month') {
    return cursor.format('MMMM YYYY')
  }

  // Day view spells the weekday out in full
  if (view === 'day') {
    return cursor.format('dddd, D MMMM YYYY')
  }

  const start = startOfWeek(cursor, weekStart)
  const end = start.add(6, 'day')

  // Weeks spanning two years need both years
  if (start.year() !== end.year()) {
    return `${start.format('D MMM YYYY')} - ${end.format('D MMM YYYY')}`
  }

  // Weeks spanning two months repeat the month but share the year
  if (start.month() !== end.month()) {
    return `${start.format('D MMM')} - ${end.format('D MMM YYYY')}`
  }

  return `${start.format('D')} - ${end.format('D MMMM YYYY')}`
}

/**
 * Weekday names for the grid header, rotated to the configured first day.
 * @param weekStart - 0 = Sunday through 6 = Saturday
 * @param reference - Any date, used so names follow the active dayjs locale
 * @param format - dayjs token, 'ddd' for short names or 'dddd' for full
 */
export function getWeekdayNames(weekStart: CalendarWeekStart, reference: Dayjs, format = 'ddd'): string[] {
  const start = startOfWeek(reference, weekStart)

  return Array.from({ length: 7 }, (_, index) => start.add(index, 'day').format(format))
}
